// "Am I looking at the new build?" — answered, rather than guessed.
//
// This has cost real time three separate times. The server reports its
// own commit at /healthz, but that says what the SERVER is running, not
// what this browser is executing: a bundle held by a service worker
// cache reads as current while being weeks old, and a hard refresh does
// not always dislodge it. Comparing the commit baked INTO this bundle
// against the one the server reports is the only honest check.
//
// It shows nothing when they match, which is almost always. The whole
// value is in the rare case where somebody is looking at a page that
// cannot contain the change they were told to look for.
//
// /healthz is on the service worker's never-cache list, so this
// comparison cannot itself be answered from a stale cache.

import React, { useEffect, useState } from "react";
import { C } from "../constants.js";

// Injected by Vite at build time. "dev" locally, where there is no
// deploy to be out of step with.
const BUNDLE = typeof __BUILD_SHA__ === "string" ? __BUILD_SHA__ : "dev";

export function StaleBuildNotice() {
  const [server, setServer] = useState(null);

  useEffect(() => {
    if (BUNDLE === "dev") return;
    let cancelled = false;
    // cache: "no-store" as well as the never-cache rule, because the
    // HTTP cache is a separate layer from the service worker and this
    // check is worthless if either can answer it.
    fetch("/healthz", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.version) setServer(d.version); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!server || BUNDLE === "dev" || server === BUNDLE) return null;

  const reload = async () => {
    // Unregister first. Reloading with the old worker still in control
    // is what makes people think a hard refresh "did not work".
    try {
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // Best effort. The reload below is still worth doing.
    }
    window.location.reload(true);
  };

  return React.createElement("div", {
    style: {
      background: C.warmWhite, borderBottom: `1px solid ${C.warmGray}`,
      padding: "10px 16px", fontSize: "13px", color: C.textDark,
      display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap",
      lineHeight: 1.5,
    },
  },
    React.createElement("span", { style: { flex: 1, minWidth: "240px" } },
      React.createElement("strong", null, "This page is running older code. "),
      "The app has been updated since this tab loaded it, so anything "
      + "changed recently will not be here yet.",
      React.createElement("span", {
        style: { color: C.textLight, marginLeft: "6px",
                 fontFamily: "ui-monospace, monospace", fontSize: "11.5px" },
      }, `${BUNDLE} → ${server}`),
    ),
    React.createElement("button", {
      onClick: reload,
      style: {
        border: "none", background: C.navy, color: "#fff",
        borderRadius: "7px", padding: "7px 15px", fontSize: "12.5px",
        fontWeight: 600, cursor: "pointer", flexShrink: 0,
      },
    }, "Load the current version"),
  );
}
